import React, { useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface CategoryCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
  saving: boolean;
}

export function CategoryCreateModal({
  isOpen,
  onClose,
  onSave,
  saving
}: CategoryCreateModalProps) {
  const [formData, setFormData] = useState({
    name: '',
    display_name: '',
    description: '',
    linguistic_type: ''
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name.trim()) {
      return;
    }
    
    // Convert name to lowercase and replace spaces with underscores
    const categoryData = {
      ...formData,
      name: formData.name.toLowerCase().replace(/\s+/g, '_'),
      display_name: formData.display_name || formData.name
    };
    
    onSave(categoryData);
  };

  const handleClose = () => {
    // Reset form
    setFormData({
      name: '',
      display_name: '',
      description: '',
      linguistic_type: ''
    });
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New Category</DialogTitle>
          <DialogDescription>
            Create a new grammar category to organize your features
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Information */}
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Category Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Modal Verbs"
                required
              />
              <p className="text-xs text-muted-foreground mt-1">
                This will be converted to lowercase with underscores (e.g., "Modal Verbs" → "modal_verbs")
              </p>
            </div>

            <div>
              <Label htmlFor="display_name">Display Name</Label>
              <Input
                id="display_name"
                value={formData.display_name}
                onChange={(e) => setFormData(prev => ({ ...prev, display_name: e.target.value }))}
                placeholder="e.g., Modal Verbs"
              />
              <p className="text-xs text-muted-foreground mt-1">
                How the category should appear in the UI (defaults to category name if not provided)
              </p>
            </div>

            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description of this grammar category..."
                rows={3}
              />
            </div>

            <div>
              <Label htmlFor="linguistic_type">Linguistic Type</Label>
              <Select
                value={formData.linguistic_type}
                onValueChange={(value) => setFormData(prev => ({ ...prev, linguistic_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="syntactic">Syntactic</SelectItem>
                  <SelectItem value="morphological">Morphological</SelectItem>
                  <SelectItem value="lexical">Lexical</SelectItem>
                  <SelectItem value="pragmatic">Pragmatic</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving || !formData.name.trim()}>
              {saving ? 'Creating...' : 'Create Category'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}